import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
  forwardRef,
  RefObject,
  useImperativeHandle,
} from 'react';

import t from '../../../labels/labelling-utils';
import { IComponent } from '../../../typings/generic-component';
import { EIGHTS, FINAL, MatchPhase, QUARTERS, SEMIFINALS } from '../../../typings/match';
import { BREAKPOINTS } from '../../../utils/breakpoints';
import { getUrlStatic } from '../../../utils/placeholders-football';
import { makeClassNames } from '../../../utils/styles';
import { ICompetitionBoardCard } from '../../molecules/competition-board-card/competition-board-card';
import { CompetitionBoardMatchGroup } from '../../organisms';
import { ICustomTrackingData } from '../../../typings/competition-board';
import './competition-board.scss';

export interface IBoardRef extends HTMLElement {
  ctaWrapper: RefObject<HTMLElement>;
  competitionBoardMatchGroup: RefObject<{ ctaWrapper: HTMLElement; setCtaIsVisible: (value: boolean) => void }[]>;
  setCtaIsVisible: (isVisible: boolean) => void;
  visibleColumn: number;
  stickyContainer: RefObject<HTMLElement>;
}
interface ICompetitionBoardClasses {
  competitionBoard: string;
  stickyContainer: string;
  tabs: string;
  tab: string;
  tabActive: string;
  sponsorContainer: string;
  columnWrapper: string;
  column: string;
  columnVisible: string;
  cta: string;
  ctaWrapper: string;
}

export interface ICompetitionMatches extends ICompetitionBoardCard {
  hasLinkedMatch?: boolean;
}

export interface ICompetitionBoard extends IComponent {
  initialVisibleColumn?: number;
  board: {
    eights: ICompetitionMatches[];
    quarters: ICompetitionMatches[];
    semi: ICompetitionMatches[];
    final: ICompetitionMatches[];
  };
  boardSponsorLink: string;
  competitionId: string;
  cta?: { url: string; label: string };
  trackingData?: ICustomTrackingData;
}

export interface IMatchGroup {
  matches: ICompetitionMatches[];
  setVisibleColumn: Dispatch<SetStateAction<number>>;
  columnIndex: number;
  trackingData?: ICustomTrackingData;
}
const LOCAL_NAV_HEIGHT = 60;

const classNames = (): ICompetitionBoardClasses =>
  makeClassNames({
    competitionBoard: 'competition-board',
    stickyContainer: 'competition-board__sticky-container',
    tabs: 'competition-board__tabs',
    tab: 'competition-board__tab',
    tabActive: 'competition-board__tab competition-board__tab--active',
    sponsorContainer: 'competition-board__sponsor-container',
    columnWrapper: 'competition-board__column-wrapper',
    column: 'competition-board__column',
    cta: ['competition-board__cta'],
    ctaWrapper: ['competition-board__cta-wrapper'],
  }) as ICompetitionBoardClasses;

const CompetitionBoard = forwardRef(
  ({ board, trackingData, boardSponsorLink, initialVisibleColumn = 0, competitionId, cta }: ICompetitionBoard, ref) => {
    const cn = classNames();
    const [startX, setStartX] = useState(null);
    const [endX, setEndX] = useState(null);
    const [visibleColumn, setVisibleColumn] = useState(initialVisibleColumn);
    const [activeTab, setactiveTab] = useState('');
    const stickyContainerRef = useRef<HTMLDivElement>(null);
    const columnWrapperRef = useRef<HTMLDivElement>(null);
    const boardRef = useRef<HTMLDivElement>(null);
    const columnsRef = useRef<(HTMLDivElement | null)[]>([]);
    const competitionBoardMatchGroupRef = useRef<any[]>([]);

    const columns = [
      { matches: board?.eights, columnIndex: 0 },
      { matches: board?.quarters, columnIndex: 1 },
      { matches: board?.semi, columnIndex: 2 },
      { matches: board?.final, columnIndex: 3 },
    ];

    const ctaLabel = cta?.label ? t(cta.label as keyof typeof t) || '' : '';

    const MAX_HEIGHT = 1900;

    const advLogo = `https://${getUrlStatic()}/images/skysport/campagne/${competitionId}/boardSponsorLogo.png`;

    useImperativeHandle(ref, () => {
      return {
        competitionBoardMatchGroup: competitionBoardMatchGroupRef,
        competitionBoardColumnWrapper: columnWrapperRef,
        visibleColumn,
      };
    });

    const handleTouchStart = (e: any) => {
      setStartX(e.touches[0].clientX);
    };

    const handleTouchMove = (e: any) => {
      setEndX(e.touches[0].clientX);
    };

    const handleTouchEnd = () => {
      if (startX && endX) {
        const deltaX = endX - startX;
        if (deltaX > 50) {
          setVisibleColumn((curValue) => {
            if (curValue === 0) {
              return curValue;
            }

            return curValue - 1;
          });
        } else if (deltaX < -50) {
          setVisibleColumn((curValue) => {
            if (curValue === 3) {
              return curValue;
            }

            return curValue + 1;
          });
        }
      }
      setStartX(null);
      setEndX(null);
    };

    const getTabs = () => {
      const competitionPhases: MatchPhase[] = [EIGHTS, QUARTERS, SEMIFINALS, FINAL];
      return (
        <div className={cn.tabs} data-active-tab={activeTab}>
          {competitionPhases.map((phase: MatchPhase, index: number) => {
            return (
              <div
                onClick={() => {
                  setVisibleColumn(index);
                  setactiveTab(phase!);
                }}
                className={visibleColumn === index ? cn.tabActive : cn.tab}
                key={index}
              >
                {phase}
              </div>
            );
          })}
        </div>
      );
    };

    useEffect(() => {
      const loadedEvent = new CustomEvent('competition_board_loaded');
      window.dispatchEvent(loadedEvent);
    }, []);

    useEffect(() => {
      if (window.innerWidth >= BREAKPOINTS.TABLET) {
        columnsRef.current.forEach((ref) => {
          ref!.style.maxHeight = `${MAX_HEIGHT}px`;
        });
        return;
      }

      if (boardRef.current) {
        window.scrollTo({
          top: boardRef.current.getBoundingClientRect().top + window.scrollY - LOCAL_NAV_HEIGHT,
          behavior: 'smooth',
        });
      }

      columnsRef.current.forEach((ref, index) => {
        if (index < visibleColumn) {
          ref!.style.maxHeight = '0';
          ref!.querySelectorAll('[data-competition-board-match-group]').forEach((card) => {
            card.setAttribute('data-state', 'collapsed');
          });
        } else {
          ref!.style.maxHeight = `${MAX_HEIGHT}px`;
          ref!.querySelectorAll('[data-competition-board-match-group]').forEach((card) => {
            card.setAttribute('data-state', 'expanded');
          });
        }
      });

      if (columnWrapperRef.current) {
        Array.from(columnWrapperRef.current.children).forEach((column) => {
          (column as HTMLDivElement).style.transform = `translateX(-${visibleColumn * 100}vw)`;
        });
      }
    }, [visibleColumn, MAX_HEIGHT]);

    return (
      <div
        className={cn.competitionBoard}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        ref={boardRef}
      >
        <div className={cn.stickyContainer} ref={stickyContainerRef}>
          {getTabs()}
          {boardSponsorLink && (
            <a href={boardSponsorLink} target="_blank" rel="noreferrer" className={cn.sponsorContainer}>
              <p>{t('sponsored-by')}</p>
              <img src={advLogo} aria-hidden loading="eager" />
            </a>
          )}
        </div>

        <div ref={columnWrapperRef} className={cn.columnWrapper}>
          {columns.map(({ matches, columnIndex }) => (
            <div key={columnIndex} className={cn.column} ref={(ref) => (columnsRef.current[columnIndex] = ref)}>
              <CompetitionBoardMatchGroup
                ref={(ref) => (competitionBoardMatchGroupRef.current[columnIndex] = ref)}
                matches={matches}
                columnIndex={columnIndex}
                trackingData={trackingData}
                cta={{ url: cta?.url || '', label: ctaLabel }}
                hasLinkedMatch={Boolean(matches?.[0]?.linkedMatch)}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }
);

CompetitionBoard.displayName = 'CompetitionBoard';

export default CompetitionBoard;
